import { getCurrencySymbol } from "./currencySymbol";

export const UNIT_VALUE_DECIMALS = 8;
export const LINE_TOTAL_DECIMALS = 2;

/**
 * Commercial half-up (never half-even). Uses exponential notation so
 * `10.005` becomes `10.01` instead of the IEEE-754 `10.005 * 100` trap.
 */
export function roundHalfUp(value: number, decimals: number): number {
  if (!Number.isFinite(value) || decimals < 0) return 0;
  if (value === 0) return 0;
  const sign = value < 0 ? -1 : 1;
  const shifted = Number(`${Math.abs(value)}e${decimals}`);
  const rounded = Math.round(shifted);
  return sign * Number(`${rounded}e-${decimals}`);
}

export function quantizeUnitValue(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return roundHalfUp(value, UNIT_VALUE_DECIMALS);
}

export function parseUnitValue(
  raw: string | number | null | undefined
): number {
  if (typeof raw === "number") {
    return quantizeUnitValue(raw);
  }
  if (raw == null) return 0;
  const trimmed = String(raw).trim();
  if (!trimmed) return 0;

  let cleaned = trimmed.replace(/[^\d,.\-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === ",") {
    return 0;
  }

  if (cleaned.includes(",") && cleaned.includes(".")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(",")) {
    cleaned = cleaned.replace(",", ".");
  }

  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return 0;
  return quantizeUnitValue(parsed);
}

function countDisplayDecimals(value: number): number {
  const quantized = quantizeUnitValue(value);
  const at2 = roundHalfUp(quantized, LINE_TOTAL_DECIMALS);
  if (Math.abs(quantized - at2) < 1e-10) {
    return LINE_TOTAL_DECIMALS;
  }
  const frac = Math.abs(quantized)
    .toFixed(UNIT_VALUE_DECIMALS)
    .split(".")[1]
    .replace(/0+$/, "");
  return Math.max(
    LINE_TOTAL_DECIMALS,
    Math.min(UNIT_VALUE_DECIMALS, frac.length)
  );
}

export function formatUnitValue(
  value: number,
  currencyCode: string = "BRL"
): string {
  const symbol = getCurrencySymbol(currencyCode || "BRL");
  const quantized = quantizeUnitValue(value);
  const decimals = countDisplayDecimals(quantized);
  const formatted = quantized.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${symbol} ${formatted}`;
}

/** Editable (unprefixed) form of a stored unit value, e.g. `10,33333333`. */
export function toEditableUnitValue(value: number): string {
  const quantized = quantizeUnitValue(value);
  if (quantized === 0) return "";
  const decimals = countDisplayDecimals(quantized);
  return quantized.toLocaleString("pt-BR", {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

export function deriveUnitValue(
  total: number,
  quantity: number,
  daily: number
): number {
  const denom = Number(quantity) * Number(daily);
  if (!Number.isFinite(denom) || denom === 0) return 0;
  return quantizeUnitValue(Number(total) / denom);
}

export function calculateLineTotal(
  unitValue: number,
  quantity: number,
  daily: number
): { fullPrecision: number; rounded: number } {
  const fullPrecision =
    quantizeUnitValue(unitValue) * Number(quantity) * Number(daily);
  return {
    fullPrecision,
    rounded: roundHalfUp(fullPrecision, LINE_TOTAL_DECIMALS),
  };
}
